import { Link } from "@tanstack/react-router";
import { urlFor } from "@/lib/sanity";
import type { MarketReport, IndustryTag } from "@/lib/market-reports.functions";

type Props = { items: MarketReport[] };

const TAG_STYLES: Record<IndustryTag, string> = {
  DeFi: "bg-violet/20 text-violet-200 border-violet/40",
  "Agentic CMO": "bg-pink-soft/20 text-pink-100 border-pink-soft/40",
  "Agentic Web": "bg-sky-500/20 text-sky-100 border-sky-500/40",
  "Agentic Commerce": "bg-fuchsia-500/20 text-fuchsia-100 border-fuchsia-500/40",
  "AI Governance": "bg-amber-500/20 text-amber-100 border-amber-500/40",
  "Agent Security & Privacy": "bg-red-500/20 text-red-100 border-red-500/40",
  "Cost of AI": "bg-emerald-500/20 text-emerald-100 border-emerald-500/40",
  "AI Macro Trends": "bg-indigo-500/20 text-indigo-100 border-indigo-500/40",
  "Circular Commerce": "bg-teal-500/20 text-teal-100 border-teal-500/40",
  "Climate Tech": "bg-lime-500/20 text-lime-100 border-lime-500/40",
  "Modern Consumer": "bg-orange-500/20 text-orange-100 border-orange-500/40",
};

function formatSgt(iso: string): string {
  try {
    const d = new Date(iso);
    const datePart = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Singapore",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(d);
    const timePart = new Intl.DateTimeFormat("en-US", {
      timeZone: "Asia/Singapore",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
    return `Published ${datePart} – ${timePart} SGT`;
  } catch {
    return "";
  }
}

function resolveImage(item: MarketReport): string | null {
  if (item.heroImageUrl) return item.heroImageUrl;
  if (item.heroImage?.asset?.url) {
    try {
      return urlFor(item.heroImage).width(1600).height(900).fit("crop").auto("format").url();
    } catch {
      return item.heroImage.asset.url;
    }
  }
  return null;
}

export function MarketReportsGrid({ items }: Props) {
  if (!items.length) {
    return (
      <section className="px-6 py-24 text-center text-white/60">
        No market reports published yet.
      </section>
    );
  }

  return (
    <section className="px-6 py-20" aria-labelledby="market-reports-grid-heading">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <h2
            id="market-reports-grid-heading"
            className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance"
          >
            Latest Market Intelligence
          </h2>
          <p className="text-sm text-white/55">
            {items.length} report{items.length === 1 ? "" : "s"} · ordered by publish time
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => {
            const img = resolveImage(item);
            const slug = item.slug?.current;
            const tagClass =
              TAG_STYLES[item.industryTag] ?? "bg-white/10 text-white border-white/20";

            return (
              <article
                key={item._id}
                className="group glass-card overflow-hidden hover-lift hover:-translate-y-1 hover:border-white/25 transition-all duration-300 flex flex-col relative"
              >
                <div className="relative aspect-video overflow-hidden bg-black/40">
                  {img ? (
                    <img
                      src={img}
                      alt={item.title}
                      loading="lazy"
                      width={1600}
                      height={900}
                      className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : null}
                  <div className="absolute top-3 left-3">
                    <span
                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold tracking-wide ${tagClass}`}
                    >
                      {item.industryTag}
                    </span>
                  </div>
                </div>

                <div className="p-6 flex flex-col gap-3 flex-1">
                  {/* Stretched link: the title link's ::after overlay makes the whole
                      card clickable without nesting other anchors inside an <a>. */}
                  <h3 className="text-lg md:text-xl font-semibold text-white leading-snug text-balance">
                    {slug ? (
                      <Link
                        to="/market-reports/$slug"
                        params={{ slug }}
                        className="after:absolute after:inset-0 hover:text-pink-soft transition-colors"
                      >
                        {item.title}
                      </Link>
                    ) : (
                      item.title
                    )}
                  </h3>
                  {item.summary ? (
                    <p className="text-sm text-white/70 leading-relaxed line-clamp-3">
                      {item.summary}
                    </p>
                  ) : null}

                  <div className="mt-auto pt-4 border-t border-white/10 flex flex-col gap-1.5 text-xs">
                    <div className="text-white/80">
                      By{" "}
                      {item.authorSocialUrl ? (
                        <a
                          href={item.authorSocialUrl}
                          target="_blank"
                          rel="author noopener noreferrer"
                          className="relative z-10 font-medium text-white hover:text-pink-soft underline-offset-2 hover:underline"
                        >
                          {item.authorName}
                        </a>
                      ) : (
                        <span className="font-medium text-white">{item.authorName}</span>
                      )}
                    </div>
                    <time dateTime={item.publishedAt} className="text-white/55 tabular-nums">
                      {formatSgt(item.publishedAt)}
                    </time>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
